import type { InternalNote } from "@nlc/shared-types";

import { useState } from "react";

import { Loader } from "@/components/Loader";
import { toast } from "@/components/toast/toast-queue";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { DeleteNoteModal } from "@/features/projects/project/internalNotes/DeleteNoteModal";

import { useDeleteRequestInternalNote } from "../hooks/useDeleteRequestInternalNote";
import { useGetRequestInternalNotes } from "../hooks/useGetRequestInternalNotes";
import { NoteCard } from "./NoteCard";

type NotesProps = {
  onEdit: (note: InternalNote) => void;
  projectId: number;
  requestId: number;
};

export default function NoteList({ onEdit, requestId }: NotesProps) {
  const { data: notes, isLoading } = useGetRequestInternalNotes(requestId);
  const deleteNote = useDeleteRequestInternalNote(requestId);

  const { canAccess } = useRoleAccess();
  const canEdit = canAccess("internal-notes.edit");

  const [deletingNote, setDeletingNote] = useState<InternalNote | null>(null);

  function handleCopyLink() {
    const url = `${window.location.origin}/requests/${requestId}/edit?internal-notes=true`;

    navigator.clipboard.writeText(url).then(() => {
      toast.add(
        { title: "Link copied to clipboard", type: "success" },
        { timeout: 3000 },
      );
    });
  }

  if (isLoading) {
    return <Loader className="h-full" />;
  }

  if (!notes || notes.length === 0) {
    return (
      <p className="text-sm text-text-secondary">
        No notes yet. Add one using the button below.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        {notes.map((note) => (
          <NoteCard
            canEdit={canEdit}
            isDeleting={deleteNote.isPending}
            key={note.id}
            note={note}
            onCopyLink={handleCopyLink}
            onDelete={setDeletingNote}
            onEdit={onEdit}
          />
        ))}
      </div>

      <DeleteNoteModal
        isDeleting={deleteNote.isPending}
        isOpen={deletingNote !== null}
        onConfirm={() => {
          if (deletingNote) deleteNote.mutate(deletingNote.id);
          setDeletingNote(null);
        }}
        onOpenChange={(isOpen) => {
          if (!isOpen) setDeletingNote(null);
        }}
      />
    </>
  );
}
